/**
 * Carousel Component
 * @module patterns/components/carousels/carousel
 */

// Dependencies
import Glide from '@glidejs/glide';

import { markElementReady } from '@root/src/framework/scripts/helpers/element-ready';
import { type App } from '~/app';

import { OPTIONS } from './constants';


export const carousels: Glide[] = [];

export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Number Panels Carousel Component');

    app.afterMount
        .then(() => mountCarousel() )
        .catch((error) => {
            throw new Error('[Stockland FE] Unable to register Number Panels Component', error);
        });

    return () => {
        while(carousels.length > 0) {
            const c = carousels.pop();
            if (c) {
                c.destroy();
            }
        }

        app.logger.debug('[DXA] Number Panels Component destroyed');
    };
}

// Loop through and individually instantiate each Glide instance
const initialiseMultiImageCarousel = (element: HTMLElement, index: number): Glide => {
    const indexClass = `number-panels-carousel-${index}`;
    element.classList.add(indexClass);

    const setButtonStyling = (index: number) => {
        const buttons = document.querySelectorAll<HTMLElement>('.number-panels-carousel-button');
        const workplaceButtons = document.querySelectorAll<HTMLElement>('.number-panels-carousel-button-workplace');
        buttons.forEach((button: HTMLElement) => {
            if (button) {
                button.dataset.theme = 'btn--style-2';
                button.classList.remove('active');
            }
        });
        workplaceButtons.forEach((button: HTMLElement) => {
            if (button) {
                button.dataset.theme = 'btn--style-4';
            }
        });
        const button = document.getElementById(`number-panels-carousel-button-${index}`);
        const workplaceButton = document.getElementById(`number-panels-carousel-button-${index}-workplace`);
        if(button && workplaceButton) {
            button.dataset.theme = 'btn--style-1';
            button.classList.add('active');
            workplaceButton.dataset.theme = 'btn--style-1';
        }
    }

    // @ts-expect-error - Types of property 'classes' are incompatible.
    const glide = new Glide(`.${indexClass}`, {
        ...OPTIONS,
        type: 'slider',
        perView: 1,
        gap:  32,
        autoplay: false,
        animationDuration: 400,
        animationTimingFunc: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
        peek: {
            before: 0,
            after: 300
        },
        breakpoints: {
            550: {
                gap:  16,
                peek: {
                    before: 0,
                    after: 80
                }
            },
            655: {
                gap:  24,
                peek: {
                    before: 0,
                    after: 150
                }
            },
            768: {
                gap:  24,
                peek: {
                    before: 0,
                    after: 300
                }
            },
            900: {
                gap:  24,
                peek: {
                    before: 0,
                    after: 24
                }
            },
            1024: {
                gap:  24,
                peek: {
                    before: 0,
                    after: 100
                }
            },
            1280: {
                gap:  24,
                peek: {
                    before: 0,
                    after: 200
                }
            },
            1440: {
                gap:  24,
                peek: {
                    before: 0,
                    after: 300
                }
            },
        },
    });

    // // On each slide click get the slide index and set it active
    for (const slides of element.querySelectorAll('.number-panels-carousel__slides')) {
        slides.addEventListener('click', function (e) {
            const slideNode = (e.target as HTMLElement).closest('.carousel__slide');
            // eslint-disable-next-line unicorn/prefer-dom-node-dataset, unicorn/prefer-number-properties
            const ind = Number(slideNode?.getAttribute('data-index'));
            if (ind === 0 || ind > 0) {
                glide.go(`=${ind - 1}`);
            }
        });
    }

    // Scrolls active tab into view when slide is active
    glide.on(['run'], function () {
        setButtonStyling(glide.index);
    })

    glide.mount();

    setButtonStyling(0);

    markElementReady(element);

    return glide;
};

export function mountCarousel() {
    const elements: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(`.number-panels-carousel`);
    for (const [index, element] of elements.entries()) {
        carousels.push(initialiseMultiImageCarousel(element, index));
    }
}